import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Grid } from "@mui/material";
import MenuOption from "../menuOption";
import { useEffect, useState } from "react";

/**
 * Componente que renderiza las opciones de menú de nivel 2 con sus opciones hijas de nivel 3 si las tiene
 * Éstas opciones se deben agregar desde la sección de opciones de menú en la plataforma por parte del administrador
 *    -------------------------- PROPS ---------------------
 * selectedOption: opción de menú seleccionada
 * i18n: objeto con las traducciones
 * navigate: función que permite navegar a la ruta seleccionada
 * fullNameUser: nombre completo del usuario en sesión
 * emailUser: correo del usuario en sesión
 * excludeUrl: función que permite excluir una url de las opciones de menú
 * homeRoutes: rutas de inicio de la aplicación
 * setOpen: función que permite abrir o cerrar el menú para ver las opciones
 * openQlikSense: función que permite abrir Qlik Sense
 * idToken: token de autenticación
 * fetchTeamcore: función que permite iniciar sesión en Teamcore
 * role: rol del usuario en sesión
 * isHD: booleano que indica si la pantalla es HD o no
 */
const MenuOptionsLvl2Desktop = ({
  selectedOption,
  i18n,
  navigate,
  fullNameUser,
  emailUser,
  excludeUrl,
  homeRoutes,
  setOpen,
  openQlikSense,
  idToken,
  fetchTeamcore,
  role,
  overFlowLengthDescription,
  isHD,
}) => {
  const [, setDesktop] = useState(window.innerWidth > 768);

  /**
   * Actualizar si es desktop o no según el tamaño de la ventana
   */
  const updateMedia = () => {
    setDesktop(window.innerWidth > 768);
  };

  /**
   * useEffect to update the state of isDesktop based on the size of the browser window
   */
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  });

  /** Modifica la altura del menú según si la descripción se desborda. */
  const modifyHeight = (isOverFlow) => {
    let height = "";
    if (isOverFlow) {
      height = isHD ? "70rem" : "32.4rem";
    } else {
      height = isHD ? "74rem" : "36.8rem";
    }
    return height;
  };

  /**
   * Función para determinar redirección de Qlik Sense
   * @param {*} urlQlik url de qlik para la opción de menú
   */
  const checkRedirectQlik = (urlQlik, optionName, item) => {
    // opciones de menú con url de qlik dinámica
    const optionsDynamicUrlQlik = ["reporte en línea", "preguntas de negocio"];
    // Verificar si la opción de menú es una opción de menú con url qlik dinámica
    // y si la url no está vacia
    if (optionsDynamicUrlQlik.includes(optionName) && urlQlik !== "") {
      openQlikSense(fullNameUser, emailUser, urlQlik);
    } else {
      if (item.url.includes("https://prescriptivalatam.us.qlikcloud.com")) {
        openQlikSense(fullNameUser, emailUser, item.url);
      } else {
        window.open(item.url, "_blank", "noopener,noreferrer");
      }
    }
  };

  /**
   * Función que valida las acciones a realizar al dar clic en una opción de menú
   * @param {object} item objeto con información de la opción de menú seleccionada
   */
  const handleOptionClick = (item) => {
    // Si el rol es admin se navega a la ruta de la opción
    // y ocultar las opciones hijas
    if (role === "admin") {
      navigate(item.navigateTo);
      setOpen(false);
    } else {
      if (
        item.url.includes("localhost") ||
        item.url.includes("innovation-dev-preescriptiva-front") ||
        item.url.includes("dev-prescriptiva-fronted.innovacioncarvajal") ||
        item.url.includes("test-prescriptiva-fronted.innovacioncarvajal") ||
        item.url.includes("prescriptivalatam.com")
      ) {
        let url = new URL(item.url).pathname;
        setOpen(false);

        navigate(url);
      } else {
        // nombre de las opciones de menú para
        // para obtener la url de session storage
        const nameEnglishOptions = {
          "reporte en línea": "onlineReport",
          "preguntas de negocio": "bussinessQuestion",
        };
        let optionName = item.menuOptionName.toLowerCase();

        // Obtener la url de qlik de la empresa para la opción de menú
        let urlQlik = sessionStorage.getItem(nameEnglishOptions[optionName]) ?? "";
        checkRedirectQlik(urlQlik, optionName, item);
        setOpen(false);
      }
    }
  };

  return (
    <Grid container>
      <Grid
        item
        height={modifyHeight(overFlowLengthDescription(selectedOption))}
        className="scroll-sidebar"
      >
        -
        {selectedOption.levels &&
          i18n &&
          selectedOption.levels.map((item, idx) => {
            if (item.levels && item.levels.length > 0) {
              //Renderiza lvl2 con hijos
              return (
                <Grid item key={idx} width="100%">
                  {/**
                   * Accordion para opciones NIVEL 2 con opciones hijas (nivel 3)
                   * */}
                  <Accordion className="accordion__menuParent__rounded">
                    <AccordionSummary
                      className="accordion__menuParent"
                      expandIcon={<ExpandMoreIcon sx={{ color: "#2E3043" }} />}
                    >
                      <Button
                        className="sideBar__button__options"
                        disableElevation={true}
                        disableRipple={true}
                        disableFocusRipple={true}
                        id={`menu-options-2-${idx}`}
                      >
                        {i18n.language.includes("es") ? item.menuOptionName : item.nameEnglish}
                      </Button>
                    </AccordionSummary>
                    <AccordionDetails className="accordion__menuParent__content">
                      <Grid
                        container
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        {/**
                         * Botones de NIVEL 3
                         */}
                        {item?.levels
                          ?.filter((item) => excludeUrl(item.url, homeRoutes) === false)

                          .map((item, idx) => {
                            return (
                              <MenuOption
                                key={idx}
                                idx={idx}
                                item={item}
                                setOpen={setOpen}
                                navigate={navigate}
                                i18n={i18n}
                                fullNameUser={fullNameUser}
                                emailUser={emailUser}
                                openQlikSense={openQlikSense}
                                role={role}
                              />
                            );
                          })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            } else {
              return (
                <Grid item key={idx}>
                  {/**
                   * Botones NIVEL 2 SIN hijos
                   */}
                  {!item?.url?.includes("teamcore") ? (
                    excludeUrl(item.url, homeRoutes) === false ? (
                      <Grid item key={idx} width="100%">
                        <Accordion className="accordion__menuParent__rounded">
                          <AccordionSummary
                            className="accordion__menuParent"
                            expandIcon={<ExpandMoreIcon sx={{ visibility: "hidden" }} />}
                          >
                            <Button
                              className="sideBar__button__options"
                              disableElevation={true}
                              disableRipple={true}
                              disableFocusRipple={true}
                              id={`menu-options-2-${idx}`}
                              onClick={() => {
                                handleOptionClick(item);
                              }}
                            >
                              {i18n.language.includes("es")
                                ? item.menuOptionName
                                : item.nameEnglish}
                            </Button>
                          </AccordionSummary>
                        </Accordion>
                      </Grid>
                    ) : null
                  ) : (
                    <form
                      action="https://auth.teamcore.net/1/public/session"
                      method="POST"
                      id="teamcore-form"
                      target="_blank"
                    >
                      <Button
                        className="sideBar__button__options"
                        disableElevation={true}
                        disableRipple={true}
                        disableFocusRipple={true}
                        onClick={fetchTeamcore}
                        id={`menu-options-2-${idx}`}
                      >
                        <input type="hidden" name="id_token" id="id_token" value={idToken} />

                        {i18n.language.includes("es") ? item.menuOptionName : item.nameEnglish}
                      </Button>
                    </form>
                  )}
                </Grid>
              );
            }
          })}
      </Grid>
    </Grid>
  );
};

export default MenuOptionsLvl2Desktop;